"use client";

import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ModalName, ModalPayload } from "./modal-types";
import {
  MODAL_TITLE_NAMESPACES,
  getModalComponent,
  getModalMaxWidth,
  getModalTitleKey,
} from "./modal-registry";

import "./modal-config";
import ModalContainer from "./modal-container";

type ModalState<T extends ModalName = ModalName> = {
  name: T;
  payload: ModalPayload<T>;
} | null;

type ModalContextType = {
  openModal: <T extends ModalName>(name: T, payload: ModalPayload<T>) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextType>({
  openModal: () => {},
  closeModal: () => {},
});

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modal, setModal] = useState<ModalState>(null);
  const { t } = useTranslation(MODAL_TITLE_NAMESPACES);

  const openModal = useCallback(
    <T extends ModalName>(name: T, payload: ModalPayload<T>) => {
      setModal({ name, payload });
      console.log(`[ModalProvider]: Name of modal - ${name}`);
    },
    [],
  );

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  const ModalComponent = modal?.name ? getModalComponent(modal.name) : undefined;
  const maxWidth = modal?.name ? getModalMaxWidth(modal.name) : undefined;
  const titleKey = modal?.name ? getModalTitleKey(modal.name) : undefined;
  const title = titleKey ? t(titleKey) : undefined;

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <>
        {ModalComponent && (
          <ModalContainer onClose={() => setModal(null)} maxWidth={maxWidth} title={title}>
            <>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-expect-error */}
              <ModalComponent payload={modal?.payload} onClose={closeModal} />
            </>
          </ModalContainer>
        )}
      </>
    </ModalContext.Provider>
  );
};
